import { useEffect, useRef, useState } from "react";
import { useStatsigClient, StatsigUser } from "@statsig/react-bindings";
import { Quote } from "../types/Quote.interface";
import isEqual from "react-fast-compare";
import { StatsigClientUtils } from "../utils/StatsigClientUtils";
import { StateUtils } from "../utils/StateUtils";

interface UseStatsigUserUpdate {
    user?: StatsigUser;
    quote?: Quote;
    /**
     * ENG-20300: the rating state resolved from the zip the quoter is typing (`appState.liveRatingState`),
     * known BEFORE a quote exists. The live zip is the quoter's latest intent and wins over the stored
     * state (same precedence as the multi-pet discount copy, ENG-19282).
     */
    ratingState?: string;
    /**
     * The states whose `ratingState` is pushed BEFORE a quote exists: those a state-targeted experiment
     * decides on the first quote write (e.g. `forms-main-ca-quoters`, from `useCaDefaultDeductible`).
     * Each pre-quote push is an /initialize round trip of its own, so any other state waits for the quote,
     * where it rides the quote-attribute update. Only membership is read, during render (the effect depends
     * on the derived state, not the array), so an inline array is fine.
     */
    preQuoteRatingStates?: readonly string[];
}

export const useStatsigUserUpdate = ({ quote, ratingState: liveRatingState, preQuoteRatingStates }: UseStatsigUserUpdate) => {
    const userUpdatingRef = useRef(false);
    // Set when an attribute change was skipped because an update was in flight; consumed on settle.
    const skippedWhileUpdatingRef = useRef(false);
    // Bumped when an in-flight `updateUserAsync` settles. Attribute changes that arrive while an update
    // is in flight are skipped below (`userUpdatingRef`); without this the effect has nothing to re-run
    // on once the update lands, and those attributes stay stale for the session. (The pre-refactor
    // version re-ran on the `user` dep, which changed as a side effect of the update.)
    const [settledUpdates, setSettledUpdates] = useState(0);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    const { policies } = quote || {};
    const { client } = useStatsigClient();
    const {
        quoteHasCats,
        quoteHasDogs,
        quotePetCount,
        breedsInQuote,
        priorityCodeLowerCase,
        ratingState: quoteRatingState
    } = StatsigClientUtils.getStatsigQuoteAttributes({ quote });
    const policyCount = policies?.length ?? 0;
    const resolvedRatingState = StateUtils.normalizeStateCode(liveRatingState) ?? quoteRatingState;
    const ratingState = policyCount > 0 || (!!resolvedRatingState && !!preQuoteRatingStates?.includes(resolvedRatingState)) ? resolvedRatingState : undefined;
    // ENG-18844: normalize to the base account form so the client-fallback path
    // (server prefetch failed, client fetch delivered a possibly suffixed
    // accountID) installs the same unit the server bootstrap and the Member
    // Center use — otherwise the surfaces get different allocations.
    const quoteAccountId = StatsigClientUtils.normalizeAccountId(quote?.accountID);

    useEffect(() => {
        // Before a quote exists only the rating state can be known; everything else waits for policies.
        if (!policyCount && !ratingState) return;
        let cancelled = false;

        const updateUser = async (nextUser: StatsigUser) => {
            if (!client) {
                console.log(`No Statsig client found`);
                return;
            }
            let succeeded = false;
            try {
                succeeded = (await client.updateUserAsync(nextUser)).success;
            } catch (err) {
                // Release the guard so the NEXT attribute change can retry, but do not re-run now: with the
                // client's user unchanged, an immediate re-run would only repeat the failing request.
                console.error(`Failed to update Statsig user: ${err}`);
                userUpdatingRef.current = false;
                return;
            }
            userUpdatingRef.current = false;
            const newerInputsArrived = skippedWhileUpdatingRef.current;
            skippedWhileUpdatingRef.current = false;
            // Deliberately NOT gated on `cancelled`: the case this serves is exactly the one where the deps
            // changed (and this effect was cleaned up) while the update was in flight. Re-running compares the
            // client's now-updated user against the current attributes, so a converged state is a no-op.
            // Re-armed on `success`, or once for inputs that arrived during the flight. Not unconditionally:
            // the no-op client StatsigContext falls back to when there is no bootstrap (Statsig disabled,
            // server fetch failed or timed out) resolves with success=false and a user that never changes, so
            // re-arming on it would compare, update, settle and re-arm forever — a render loop on every step
            // that feeds this hook. The real client reports false only when the network gave it nothing, where
            // an immediate re-run would just repeat the failing request; inputs skipped during that request
            // still get their one retry, so a failed update cannot leave them stale until the next change.
            if (mountedRef.current && (succeeded || newerInputsArrived)) setSettledUpdates(count => count + 1);
        };

        // Deferred past the current passive-effect flush so a parent provider's synchronous user re-apply
        // (`BootstrappedStatsigProvider`: setData + updateUserSync in *its* effect, which React flushes
        // after this child effect) has already run, and read the client's live user at that moment rather
        // than this render's snapshot. Replaces the one-pass "arm" state ThankYou used to hold for the
        // same purpose. `useStatsigUser` reads the same `client.getContext().user`, memoized per render.
        queueMicrotask(() => {
            if (cancelled) return;

            const user = client.getContext().user;
            if (!user) return;

            const hasQuote = policyCount > 0;
            const needsAccountIdUpdate = hasQuote && !!quoteAccountId && !user.customIDs?.accountID;

            // The quote-derived attributes are only written once a quote exists: computed from no quote they
            // would read "spot" / no pets and overwrite the server bootstrap's URL-aware values.
            const needsQuoteAttributesUpdate =
                hasQuote &&
                (needsAccountIdUpdate ||
                    user.custom?.priorityCodeLowerCase !== priorityCodeLowerCase ||
                    user.custom?.quoteHasDogs !== quoteHasDogs ||
                    user.custom?.quoteHasCats !== quoteHasCats ||
                    user.custom?.quotePetCount !== quotePetCount ||
                    !isEqual(user.custom?.breedsInQuote, breedsInQuote));
            // Never cleared: an unresolved zip keeps the last known state rather than dropping the quoter
            // out of a state-targeted experiment mid-edit.
            const needsRatingStateUpdate = !!ratingState && user.custom?.ratingState !== ratingState;

            const needsUpdate = needsQuoteAttributesUpdate || needsRatingStateUpdate;
            if (needsUpdate && userUpdatingRef.current) {
                // Newer inputs while an update is in flight: remembered so the settle re-arms this effect once
                // even when that update fails, instead of leaving them stale until the next input change.
                skippedWhileUpdatingRef.current = true;
                return;
            }
            if (needsUpdate) {
                userUpdatingRef.current = true;
                const updatedUser = {
                    ...user,
                    ...(needsAccountIdUpdate ? { customIDs: { ...user.customIDs, accountID: quoteAccountId } } : {}),
                    custom: {
                        ...user.custom,
                        ...(hasQuote
                            ? {
                                  priorityCodeLowerCase,
                                  quoteHasDogs,
                                  quoteHasCats,
                                  quotePetCount,
                                  breedsInQuote
                              }
                            : {}),
                        ...(ratingState ? { ratingState } : {})
                    }
                };
                updateUser(updatedUser);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [client, policyCount, quoteHasCats, quoteHasDogs, quotePetCount, breedsInQuote, priorityCodeLowerCase, quoteAccountId, ratingState, settledUpdates]);
};
